import { Staleable } from 'interfaces/global'
import { OrderingApiPayload, PageSizeApiPayload, CursorBasedApiPayload } from 'interfaces/ajax/payload'
import { CursorPaginatable, BasePaginationState, RawForwardPaginatable } from 'interfaces/table'
import { PeergroupRelation, PeergroupId, RelationId, PeergroupDefinition, Peergroup } from 'interfaces/peergroups'
import { ImageId, ImageURLs, MediaLink, MediaLinkId } from 'interfaces/media'
import { Collection, StoreSet, Omit } from 'interfaces'
import { UserImageId } from 'interfaces/userImage'
import { ViewCommentCTA, Insight } from 'interfaces/insights'
import { WithRequestStateDetails, WithRequestDetails } from 'interfaces/ajax/base'
import { Option } from 'interfaces/component'
import { SocialInsight } from 'interfaces/socialInsights'

export type CommentUid = string
export type PostUid = string
export type FeedUserUid = string
export type FeedFilterId = number
export type ComposeId = string

export type WithComposeId = {
  composeId: ComposeId
}

export type WithType<T> = {
  type: T,
}

export type FeedUserResource = {
  type: 'user'
  name: string
}
export type FeedTagResource = {
  type: 'tag'
  name: FeedTagName
}
export type FeedPostResource = {
  type: 'post'
  postUid: PostUid
}
export type FeedResource =
  | FeedUserResource
  | FeedTagResource
  | FeedPostResource

export type WithLinks = {
  links: MediaLink[]
}

export type WithImages = {
  images: ImageURLs[]
}

export type WithUserUid = {
  userUid: FeedUserUid
}

export type WithImagesIds = {
  imagesIds: ImageId[]
}

export type WithUsername = {
  username: string
}

export type WithTagname = {
  tagName: string
}

export type Attachments =
  & WithLinks
  & WithImages


export type ComposeOutput = {
  text: string | null
  tags?: string[]
  image_ids: ImageId[]
  link_ids: MediaLinkId[]
}

export type FeedCommentInput = ComposeOutput & {
  post: PostUid
  parent_uid?: CommentUid
}

export type FeedPostTargetInput = {
  filter_by: string
  slug?: FeedgroupSlug
  id?: PeergroupId
}

export type FeedPostInput = ComposeOutput & {
  target: FeedPostTargetInput
}

// btw "author" is defined on client side
type FeedBadgeSlug = '1000+likes' | 'verified' | 'author' | '' | 'premium'
export type FeedBadge = {
  slug: FeedBadgeSlug
  text: string
}

type BaseFeedUser = {
  uid: FeedUserUid
  username: string
  badges: FeedBadge[]
  is_followed: boolean
  peergroup?: MatchingPeergroup | null
  posts_count: number
  likes_count: number
  followers_count: number
}

export type RawFeedUser =
  & BaseFeedUser
  & {
    avatar: ImageURLs | null
  }

export type FeedUser =
  & BaseFeedUser
  & {
    avatarId: UserImageId | null
  }

export type SuggestUser = Pick<RawFeedUser, 'uid' | 'username' | 'avatar' | 'badges'>
export type SuggestTag = {
  name: FeedTagName
  posts_count: number
  is_followed: boolean
}

export type SelectableFeedFilterMe = {
  type: 'your-posts'
}

export type SelectableFeedFilterPeergroup = {
  type: 'peergroup'
  relationId: RelationId
}

export type FeedgroupSlug =
  | 'followed_tags'
  | 'followed_posts'
  | 'followed_people'
  | 'everyone'

export type SelectableFeedFilterFeedgroup = {
  type: 'feedgroup'
  slug: FeedgroupSlug
}

export type SelectableFeedFilter =
  | SelectableFeedFilterMe
  | SelectableFeedFilterPeergroup
  | SelectableFeedFilterFeedgroup

export type SaturatedSelectableFeedFilter =
  SelectableFeedFilter
  & {
    relation: PeergroupRelation | null
  }

type RawCommentsThread = RawForwardPaginatable<RawComment>
export type CommentsThread =
  & CursorPaginatable<CommentUid>
  & WithRequestStateDetails

type BaseComment =
  & {
    uid: CommentUid
    text: string
    created: string
    modified?: string
    likes_count: number
    is_liked: boolean
    is_reported: boolean
    peergroup?: MatchingPeergroup
  }

export type RawOwnComment =
  & BaseComment
  & WithLinks
  & WithImages
  & {
    mentions: RawMentions
    parent?: CommentUid
  }

export type RawComment =
  & RawOwnComment
  & {
    author: RawFeedUser
    children?: RawCommentsThread
    children_count?: number
  }

export type WithPostUid = {
  postUid: PostUid
}

export type WithCommentUid = {
  commentUid: CommentUid
}

export type Comment =
  & BaseComment
  & WithLinks
  & WithImagesIds
  & WithPostUid
  & {
    authorUid: FeedUserUid
    mentions: Mention[]
    children: CommentsThread | null
    childrenCount: number
  }

export type RawMentions = Record<FeedUserUid, Pick<FeedUser, 'username'>>

export const POST_TYPE_POST = 'post' as const
export const POST_TYPE_SPONSORED_POST = 'sponsored_post' as const
export const POST_TYPE_SOCIAL_INSIGHT = 'social_insight' as const

type BasePost = {
  uid: PostUid
  created: string
  followers_count: number
  likes_count: number
  comments_count: number
  text: string
  is_liked: boolean
  is_followed: boolean
  is_reported: boolean
  is_edited: boolean
  peergroup?: MatchingPeergroup
  short_url: string
  type: typeof POST_TYPE_POST
}

type MatchingPeergroupMatch = {
  ratio: number
  parameters: (keyof PeergroupDefinition)[]
}

export type MatchingPeergroupFilled = {
  match: MatchingPeergroupMatch
  parameters: PeergroupDefinition
}

export type MatchingPeergroupEmpty = {
  match: null
  parameters: PeergroupDefinition
}

export type MatchingPeergroup = MatchingPeergroupFilled | MatchingPeergroupEmpty

export type RawPost =
  & BasePost
  & WithLinks
  & WithImages
  & {
    author: RawFeedUser
    comments: RawCommentsThread
    comments_ordering: string[]
    mentions: RawMentions
  }

export type WithCommentsThread<TNullable extends boolean = false> = {
  comments: TNullable extends true ? null | CommentsThread : CommentsThread
  commentsOrdering: FeedOrdering
}

export type PostNormalized =
  & BasePost
  & WithLinks
  & WithImagesIds
  & WithCommentsThread
  & {
    authorUid: FeedUserUid

    mentions: Mention[]
  }

export type InsightPostNormalized =
  & Omit<PostNormalized, 'type'>
  & {
    type: typeof POST_TYPE_SPONSORED_POST
    insight: Insight
  }

export type Post =
  | PostNormalized
  | InsightPostNormalized
  | SocialInsight

export type PostsCollection = Collection<Post, 'uid'>

export type FeedPeergroupQuery = {
  filter_by: 'peergroup'
  id: number
  loose: boolean
}
type FeedFeedgroupQuery = {
  filter_by: 'feedgroup'
  slug: FeedgroupSlug
}
type FeedMemberUserQuery = {
  filter_by: 'user'
  uid?: string
  username?: string
}
type FeedTagQuery = {
  filter_by: 'tag'
  name: string
}
type FeedPostQuery = {
  filter_by: 'post'
  uid: PostUid
}

export type PaginatedApiPayload =
  & OrderingApiPayload
  & PageSizeApiPayload

export type FeedFilterQuery =
  | FeedPeergroupQuery
  | FeedFeedgroupQuery
  | FeedMemberUserQuery
  | FeedTagQuery
  | FeedPostQuery

export type GetPostsApiInitialPayload =
  & PaginatedApiPayload
  & FeedFilterQuery

export type GetPostsApiPayload =
  | GetPostsApiInitialPayload
  | CursorBasedApiPayload

type GetCommentsInitialApiPayload =
  & PageSizeApiPayload
  & {
    ordering?: string
    post_uid: PostUid
  }

export type GetCommentsApiPayload =
  | GetCommentsInitialApiPayload
  | CursorBasedApiPayload

export type GetFollowedUsersApiPayload =
  | PageSizeApiPayload
  | CursorBasedApiPayload

export type FollowedUserInfo = {
  peergroup: MatchingPeergroup
}

export type FollowedUsersLoading = BasePaginationState<FeedUserUid>

export type RawFollowedUser =
  & RawFeedUser
  & FollowedUserInfo

export type ComposeType = 'post' | 'comment'

export type Mention = Pick<FeedUser, 'uid' | 'username'>

export type ComposeInsertTextCommand =
  & WithType<'insertText'>
  & {
  text: string
}

export type ComposeReplaceTextCommand =
  & WithType<'replaceText'>
  & {
  text: string
  start: number
  end: number
}

export type ComposeCommand =
  | ComposeInsertTextCommand
  | ComposeReplaceTextCommand
  | WithType<'focus'>
  | WithType<'nextSuggest'>
  | WithType<'prevSuggest'>
  | WithType<'useSuggest'>
  | WithType<'startTag'>
  | WithType<'submit'>
  | WithType<'click'>


export const COMPOSE_DEFAULT_INPUT = 'default' as const
export const COMPOSE_SUGGEST_INPUT = 'suggest' as const
export type ComposeInputBehavior =
  | typeof COMPOSE_DEFAULT_INPUT
  | typeof COMPOSE_SUGGEST_INPUT

export type ComposeState = {
  id: ComposeId
  isSubmitting: boolean
  isSubmitted: boolean
  errorCode: number | null
  errorExtra: string[] | null
  link: MediaLink | null
  isLinkLoading: boolean
  isLinkRemoved: boolean
  imagesIds: UserImageId[]
  mentions: Mention[]
  text: string
  isExists: boolean
  type: ComposeType
  postUid?: PostUid
  commentUid?: CommentUid
  parentCommentUid?: CommentUid
  replyCommentUid?: CommentUid    // TODO: it's to be the same prop as commentUid
  isEdit: boolean
  isFocused: boolean
  filter?: SelectableFeedFilter
  execCommand: ComposeCommand | null
  inputBehavior: ComposeInputBehavior

  placeholder?: string,
  focusOnMount?: boolean,
  isOpportunityReview?: boolean
}

export type ComposesState = Collection<ComposeState, 'id'>

export type CountData = {
  count: number
}

export type PostCommentsLoadingIds = StoreSet<PostUid>
export type LikingCommentUids = StoreSet<CommentUid>
export type LikingPostUids = StoreSet<PostUid>
export type AuthorFollowingLoadingIds = StoreSet<FeedUserUid>
export type PostFollowingLoadingIds = StoreSet<FeedUserUid>
export type EditingPostUids = StoreSet<PostUid>
export type EditingCommentUids = StoreSet<CommentUid>

export type FeedfilterPopupState = {
  shownFilter: SelectableFeedFilter | null
  rect: ClientRect | null
  hideTimeoutId: number | null
}

export type FollowedPostsCount = {
  posts_count: number
  new_comments_count: number
}

export type FilterUpdates = Partial<Record<string, boolean>>
export type FeedFilterStatus = {
  is_updated: boolean
}

export type SocialProfileStep = 'form' | 'confirmation' | 'cropper'
export type SocialProfileState = {
  step: SocialProfileStep | null
  focusComposeId?: ComposeId
}
export const FEED_ORDERING_BY_POPULARITY = '-popularity,created' as const
export const FEED_ORDERING_BY_CREATED = '-created' as const
export const FEED_ORDERING_CHRONOLOGICALLY = 'created' as const
export const FEED_ORDERING_RANK = 'rank' as const
export type FeedOrdering =
  | typeof FEED_ORDERING_BY_POPULARITY
  | typeof FEED_ORDERING_BY_CREATED
  | typeof FEED_ORDERING_CHRONOLOGICALLY
  | typeof FEED_ORDERING_RANK


export type GetFollowedTagsApiPayload =
  | PageSizeApiPayload
  | CursorBasedApiPayload

export type FeedTagName = string
export type FeedTag =
  & SuggestTag
  & {
    new_posts_count?: number
  }

export type TagSearchOption = Option<FeedTagName>

export type TagCollection = Collection<FeedTag, 'name'>
export type TagsOrdering = '-popularity' | '-trend'

export type TagKeysWithRequestDetails =
  & WithRequestDetails<FeedTagName[] | null>
  & { search: string }

export type TagKeysBySearch = Record<string, TagKeysWithRequestDetails>

export type FollowedTagsLoading = BasePaginationState<FeedTagName>

export type TagFollowingLoadingIds = StoreSet<FeedUserUid>


export type FilterPostsLoading =
  & WithRequestStateDetails
  & {
    pagination: CursorPaginatable<PostUid> | null
  }

export type PostByFilterState = Partial<Record<string, Staleable<FilterPostsLoading>>>

// TODO store this relation with other relations
export type RandomFeedRelationState = {
  isLoading: boolean
  isRandomizing: boolean
  data: null | {
    peergroup: Peergroup
    relation: PeergroupRelation
  }
}

export type UserSuggestsDataState = WithRequestDetails<SuggestUser[]>

export type TagsSuggestsDataState = WithRequestDetails<SuggestTag[]>

export type FeedUsersState = Collection<FeedUser, 'uid'>

export type FeedAppState = {
  // this field contains only filter selected by user,
  // default one is calculated in selector
  userSelectedFilter: SelectableFeedFilter | null
  shouldResetFollowedPeople: boolean
  shouldResetFollowedTags: boolean
  filterPopup: FeedfilterPopupState

  composes: ComposesState
  composeLastFocus: ComposeId

  socialProfile: SocialProfileState

  isFollowedPostsLoading: boolean
  isFiltersStatusLoading: boolean
  postCommentsLoadingIds: PostCommentsLoadingIds
  likingCommentUids: LikingCommentUids
  likingPostUids: LikingPostUids
  authorFollowingLoadingIds: AuthorFollowingLoadingIds
  postFollowingLoadingIds: PostFollowingLoadingIds
  tagFollowingLoadingIds: TagFollowingLoadingIds
  editingPostsIds: EditingPostUids
  editingCommentsIds: EditingCommentUids
  replyCommentsIds: EditingCommentUids
  hiddenRepliesCommentsIds: EditingCommentUids

  targetComment: ViewCommentCTA | null
}

export type FeedDataState = {
  allPosts: PostsCollection
  postsByFilter: PostByFilterState
  comments: Collection<Comment, 'uid'>
  followedPostsCount: FollowedPostsCount | null
  users: FeedUsersState
  allTags: TagCollection
  tagsBySearch: TagKeysBySearch
  filterUpdates: FilterUpdates
  followedUsersLoading: FollowedUsersLoading
  followedTagsLoading: FollowedTagsLoading
  randomFeedRelation: RandomFeedRelationState
  suggestedUsers: UserSuggestsDataState
  suggestedTags: TagsSuggestsDataState
}

export type SocialProfileFormValues = {
  username?: string | null
  avatar?: ImageId
}

export type CheckExistsRequest = {
  username: string
}

export type CheckExistsResponse = {
  exists: boolean
}
